import { useParams } from 'react-router-dom';

import { useRequete } from '../api/client';
import type { FichePersonne } from '../api/types';
import { Anneau, Badge, Chargement, Jauge, Message, dateLongue } from '../composants/elements';
import { libelleRole } from '../composants/roles';

/** Fiche d'une personne — §6.3.2 : notes, preuves, évolution trimestrielle. */
export function FichePersonnePage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const fiche = useRequete<FichePersonne>(id ? `/api/personnes/${id}/fiche` : null, [id]);

  if (fiche.erreur) {
    return (
      <div className="contenu section">
        <Message variante="erreur">{fiche.erreur.message}</Message>
      </div>
    );
  }
  if (!fiche.donnees) return <Chargement />;

  const { personne, trimestres } = fiche.donnees;

  return (
    <div className="contenu" style={{ paddingBottom: 'var(--space-9)' }}>
      <div className="entete-page">
        <div>
          <p className="eyebrow" style={{ marginBottom: 14 }}>
            Management · named profile
          </p>
          <h1 style={{ fontSize: 'clamp(28px, 3.4vw, 44px)' }}>{personne.nom}</h1>
          <p className="plomb" style={{ marginTop: 12 }}>
            {personne.intitulePoste ?? 'Role not set'} · {personne.email}
          </p>
          {/* Une fiche nominative montre toutes les appartenances : c'est là qu'on
              vient les lire. */}
          <div className="jetons" style={{ marginTop: 12 }}>
            {personne.equipes.length === 0 ? (
              <span className="discret">No team</span>
            ) : (
              personne.equipes.map((e) => (
                <span key={e.id} className="jeton" title={e.libellesTags.join(', ')}>
                  {e.nom}
                  {e.libellesTags.length > 0 ? ` · ${e.libellesTags.join(', ')}` : ''}
                </span>
              ))
            )}
          </div>
        </div>
        {/* Le libellé d'affichage, pas la valeur en base : « encadrement »
            n'est pas de l'anglais. */}
        <Badge>{libelleRole(personne.role)}</Badge>
      </div>

      {trimestres.length === 0 ? (
        <Message variante="info">This person has no assessment yet.</Message>
      ) : null}

      {trimestres.map((t) => (
        <section key={t.trimestre} style={{ paddingBottom: 48 }}>
          <div className="entete-groupe">
            <div>
              <h2 style={{ fontSize: 'clamp(20px, 2.2vw, 28px)' }}>{t.trimestre}</h2>
              <p className="discret" style={{ marginTop: 6 }}>
                {t.soumiseLe ? `Submitted on ${dateLongue(t.soumiseLe)}` : 'Not submitted'} ·{' '}
                campaign {t.etat === 'fermee' ? 'closed' : t.etat === 'ouverte' ? 'open' : 'draft'}
              </p>
            </div>
            <div style={{ minWidth: 190 }}>
              <p className="mono" style={{ fontSize: 15, textAlign: 'right', marginBottom: 6 }}>
                {t.progression.repondu} / {t.progression.total}
              </p>
              <Jauge progression={t.progression} />
            </div>
          </div>

          {t.forces.length === 0 ? (
            <p className="discret" style={{ paddingTop: 16 }}>
              No technology declared at 3 or above for this quarter.
            </p>
          ) : (
            <div className="tableau-defilant">
              <table className="tableau">
                <thead>
                  <tr>
                    <th>Technology</th>
                    <th>Ring</th>
                    <th className="tableau__nombre">Level</th>
                    <th>Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {t.forces.map((f) => (
                    <tr key={`${t.trimestre}-${f.titre}`}>
                      <td style={{ fontWeight: 500 }}>{f.titre}</td>
                      <td>
                        <Anneau anneau={f.anneau} />
                      </td>
                      <td className="tableau__nombre">{f.niveau}</td>
                      <td style={{ maxWidth: 520, color: 'var(--fg-2)' }}>{f.preuve ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
